/** For now: treat this email as administrator (bypasses role check). */
export const ADMIN_EMAIL = 'admin@icom.gr';

export function isAdminUser(user: User | null | undefined): boolean {
  return user?.role === UserRole.Administrator || user?.email === ADMIN_EMAIL;
}

export enum UserRole {
  IndividualMember = 'IndividualMember',
  InstitutionMember = 'InstitutionMember',
  Administrator = 'Administrator',
}

export enum CommunicationLanguage {
  English = 'English',
  Greek = 'Greek',
  French = 'French',
  Spanish = 'Spanish',
}

export enum RegistrationType {
  NewMember = 'NewMember',
  ExistingMember = 'ExistingMember',
  ExistingInstitution = 'ExistingInstitution',
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole | 'member' | 'staff';
  avatar?: string;
  region?: string;
  museumType?: string;
  museumName?: string;
  position?: string;
  phone?: string;
  isActive?: boolean;
  emailConfirmed?: boolean;
  /** When false, individual members are redirected to edit profile on first login. */
  profileCompleted?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  refreshToken: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  role: UserRole;
  communicationLanguage?: CommunicationLanguage;
  registrationType?: RegistrationType;
  existingMemberId?: string;
  existingInstitutionId?: string;
  firstName?: string; // Required for IndividualMember; for ExistingMember used for re-validation
  lastName?: string; // Required for IndividualMember; for ExistingMember used for re-validation
  memberCardNumber?: string; // For ExistingMember re-validation (optional - members without card can leave empty)
  institutionName?: string; // Required for InstitutionMember / ExistingInstitution re-validation
  institutionNameEn?: string;
  institutionNameGr?: string;
  institutionEmail?: string; // For ExistingInstitution re-validation
}

export interface MatchMemberRequest {
  firstName: string;
  lastName: string;
  memberCardNumber: string;
  email?: string;
  dateOfBirth?: string;
}

export interface MatchMemberResponse {
  found: boolean;
  member?: import('./member').IndividualMemberDto;
  hasUserAccount: boolean;
}

export interface MatchInstitutionRequest {
  institutionName: string;
  institutionEmail: string;
}

export interface MatchInstitutionResponse {
  found: boolean;
  institution?: import('./institution').InstitutionDto;
  hasUserAccount: boolean;
}

export interface RegisterExistingMemberRequest extends RegisterRequest {
  registrationType: RegistrationType.ExistingMember;
  existingMemberId: string;
}

export interface RegisterExistingInstitutionRequest extends RegisterRequest {
  registrationType: RegistrationType.ExistingInstitution;
  existingInstitutionId: string;
  institutionName: string;
  institutionEmail: string;
}

export interface RegisterResponse {
  user?: User;
  token?: string;
  refreshToken?: string;
  /** When true, account was created but email must be confirmed; no tokens returned. */
  requiresEmailConfirmation?: boolean;
  /** Email where confirmation was sent (when requiresEmailConfirmation is true). */
  email?: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}
